import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Sparkles, ChevronDown, ArrowRight, MapPin, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two community sections for the homepage footer area:
 *   1. Founding Vendors — top 10 by number, collapsable "View all"
 *   2. Recently Added Vendors — fully completed profiles within 24h
 *
 * Both show public cards. Clicking a vendor card prompts login/signup
 * for anonymous visitors before revealing the full profile.
 */
export default function HomeCommunitySections() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [founders, setFounders] = useState([]);
  const [recentVendors, setRecentVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllFounders, setShowAllFounders] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [f, r] = await Promise.all([
          fetch(`${API}/founding-vendors/list`).then((x) => (x.ok ? x.json() : null)),
          fetch(`${API}/vendors/recently-added?limit=10`).then((x) => (x.ok ? x.json() : null)),
        ]);
        setFounders((f?.vendors || []).filter(v => v?.profile_image));
        setRecentVendors((r?.vendors || []).filter(v => v?.profile_image));
      } catch {}
      finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleVendorClick = (vendorId) => {
    if (user) {
      navigate(`/vendor/${vendorId}`);
    } else {
      // Remember where to return after signup
      try {
        sessionStorage.setItem('postAuthRedirect', `/vendor/${vendorId}`);
      } catch {}
      navigate(`/auth?mode=register&return=${encodeURIComponent(`/vendor/${vendorId}`)}`);
    }
  };

  if (loading) return null;
  if (founders.length === 0 && recentVendors.length === 0) return null;

  const topFounders = founders.slice(0, 10);
  const extraFounders = founders.slice(10);

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-transparent via-[#0A0A1F] to-[#0E0E23]" data-testid="home-community-sections">
      <div className="max-w-7xl mx-auto space-y-16">

        {/* ─────── Founding Vendors ─────── */}
        {founders.length > 0 && (
          <div data-testid="home-founding-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="flex items-center gap-2 text-amber-400 text-[11px] font-bold uppercase tracking-widest mb-2">
                  <Award size={14} /> The Founding 50
                </div>
                <h2 className="font-syne text-3xl sm:text-4xl font-bold text-white">
                  Meet the vendors who built this platform
                </h2>
                <p className="text-zinc-500 font-outfit text-sm mt-2 max-w-xl">
                  Founding Vendors claimed their numbered seat to back EntrepreNatives at launch. Forever recognized. Forever Gold.
                </p>
              </div>
              <Link
                to="/founding-family"
                className="text-sm font-semibold text-amber-400 hover:text-amber-300 inline-flex items-center gap-1 transition-colors"
                data-testid="home-founding-see-all"
              >
                View the Wall of Fame <ArrowRight size={14} />
              </Link>
            </div>

            <motion.div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {topFounders.map((v) => (
                <FounderCard key={v.vendor_id} vendor={v} onClick={() => handleVendorClick(v.vendor_id)} isLoggedIn={!!user} />
              ))}
              {showAllFounders && extraFounders.map((v) => (
                <FounderCard key={v.vendor_id} vendor={v} onClick={() => handleVendorClick(v.vendor_id)} isLoggedIn={!!user} />
              ))}
            </motion.div>

            {extraFounders.length > 0 && (
              <div className="flex justify-center mt-5">
                <button
                  type="button"
                  onClick={() => setShowAllFounders((v) => !v)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-xs font-semibold transition-all"
                  data-testid="home-founding-toggle"
                >
                  {showAllFounders ? 'Collapse' : `Show all ${founders.length} Founding Vendors`}
                  <ChevronDown size={14} className={`transition-transform ${showAllFounders ? 'rotate-180' : ''}`} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─────── Recently Added (24h, fully complete) ─────── */}
        {recentVendors.length > 0 && (
          <div data-testid="home-recent-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="flex items-center gap-2 text-emerald-400 text-[11px] font-bold uppercase tracking-widest mb-2">
                  <Sparkles size={14} /> This Week's New Vendors · Fresh Spotlight
                </div>
                <h2 className="font-syne text-3xl sm:text-4xl font-bold text-white">
                  This Week's New Indigenous Artists &amp; Businesses
                </h2>
                <p className="text-zinc-500 font-outfit text-sm mt-2 max-w-xl">
                  Meet the newest faces on EntrepreNatives. Add a profile picture and a bit about your business to earn your spot on the homepage.
                </p>
              </div>
              <Link
                to="/browse?sort=newest"
                className="text-sm font-semibold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 transition-colors"
                data-testid="home-recent-see-all"
              >
                Browse all new vendors <ArrowRight size={14} />
              </Link>
            </div>

            <motion.div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {recentVendors.map((v) => (
                <RecentVendorCard key={v.vendor_id} vendor={v} onClick={() => handleVendorClick(v.vendor_id)} isLoggedIn={!!user} />
              ))}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}

function FounderCard({ vendor, onClick, isLoggedIn }) {
  const bg = vendor.banner_image || vendor.profile_image;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-square rounded-2xl overflow-hidden border border-amber-500/20 hover:border-amber-400/80 transition-all text-left bg-[#1E1E3A] focus:outline-none focus:ring-2 focus:ring-amber-400/50"
      data-testid={`home-founder-${vendor.vendor_id}`}
      title={`${vendor.business_name || vendor.owner_name || 'Founding Vendor'} — ${isLoggedIn ? 'View profile' : 'Sign in to view'}`}
    >
      {bg ? (
        <img
          src={bg.startsWith('/api/') || bg.startsWith('http') ? (bg.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${bg}` : bg) : bg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 via-orange-500/20 to-[#8B5CF6]/20" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 text-[10px] font-black text-black">
        #{vendor.founding_vendor_number} · GOLD
      </span>
      {!isLoggedIn && (
        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm text-white/80 opacity-0 group-hover:opacity-100 transition-opacity" title="Sign in to view profile">
          <Lock size={11} />
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
        <p className="text-white font-bold text-sm truncate leading-tight">
          {vendor.business_name || vendor.owner_name || 'Founding Vendor'}
        </p>
        {vendor.owner_name && vendor.business_name && (
          <p className="text-amber-200 text-[10px] truncate">{vendor.owner_name}</p>
        )}
        {(vendor.service_city || vendor.service_state) && (
          <p className="text-zinc-400 text-[10px] truncate flex items-center gap-1 mt-0.5">
            <MapPin size={9} /> {[vendor.service_city, vendor.service_state].filter(Boolean).join(', ')}
          </p>
        )}
      </div>
    </button>
  );
}

function RecentVendorCard({ vendor, onClick, isLoggedIn }) {
  const bg = vendor.profile_image || vendor.banner_image;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-[4/5] rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400/60 transition-all text-left bg-[#1E1E3A] focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
      data-testid={`home-recent-${vendor.vendor_id}`}
      title={`${vendor.business_name || vendor.owner_name || 'Indigenous vendor'} — ${isLoggedIn ? 'View profile' : 'Sign in to view'}`}
    >
      {bg ? (
        <img
          src={bg.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${bg}` : bg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-[#8B5CF6]/20 to-[#3B82F6]/20" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/80 backdrop-blur-sm text-[9px] font-bold text-black uppercase tracking-wider">
        New
      </span>
      {!isLoggedIn && (
        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
          <Lock size={11} />
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-2.5 z-10">
        <p className="text-white font-bold text-sm truncate leading-tight">
          {vendor.business_name || vendor.owner_name || 'New vendor'}
        </p>
        {vendor.owner_name && vendor.business_name && (
          <p className="text-emerald-300 text-[10px] truncate">{vendor.owner_name}</p>
        )}
        {vendor.location && (
          <p className="text-zinc-400 text-[10px] truncate flex items-center gap-1 mt-0.5">
            <MapPin size={9} /> {vendor.location}
          </p>
        )}
      </div>
    </button>
  );
}
